/**
 * Planning module — pure helpers (no React).
 *
 * Anything that doesn't need component state lives here:
 *  - ISO week math (weekDates, getISOWeekNow)
 *  - Static styling tables
 *  - Day-name constants in Turkish
 */

export const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
export const DAY_NAMES_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

/**
 * Pull the translated short-day label for a Monday-indexed day (0=Mon..6=Sun),
 * falling back to the hardcoded TR list if the key is missing. We use the
 * same `dashboard.daysShort` namespace the employee planning widget does so
 * there's a single source of truth. Keyed by JS getDay() (0=Sun..6=Sat).
 */
export function dayShortI18n(t, dayIdxMondayFirst) {
  const jsDow = (dayIdxMondayFirst + 1) % 7; // Mon(0)->1, ... Sun(6)->0
  const key = `dashboard.daysShort.${jsDow}`;
  const v = t ? t(key) : null;
  if (typeof v === 'string' && v && !v.startsWith('dashboard.')) return v;
  return DAY_NAMES_SHORT[dayIdxMondayFirst];
}

/** Like dayShortI18n but for short-month names. jsMonthIdx is 0-based. */
export function monthShortI18n(t, jsMonthIdx) {
  const key = `dashboard.monthsShort.${jsMonthIdx}`;
  const v = t ? t(key) : null;
  if (typeof v === 'string' && v && !v.startsWith('dashboard.')) return v;
  const fb = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  return fb[jsMonthIdx] || '';
}

/**
 * Build a compact column header for the planning board: `16 Nis Pzt`.
 * Renders in whatever language `t` currently provides.
 * iso = YYYY-MM-DD (Monday-indexed day derived from its position, NOT getDay,
 * because we always align columns Monday..Sunday regardless of locale).
 */
export function columnHeaderLabel(t, iso, dayIdxMondayFirst) {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return `${d.getDate()} ${monthShortI18n(t, d.getMonth())} ${dayShortI18n(t, dayIdxMondayFirst)}`;
  } catch { return iso; }
}

/** Convert ISO week → array of 7 YYYY-MM-DD strings starting Monday. */
export function weekDates(year, week) {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dow = simple.getUTCDay() || 7;
  const monday = new Date(simple);
  if (dow !== 1) monday.setUTCDate(simple.getUTCDate() - dow + 1);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

/** Current ISO week (year, week) for "today" in the user's local clock. */
export function getISOWeekNow() {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
}

export const STATUS_STYLES = {
  assigned:  'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-100',
  empty:     'bg-slate-50 border-dashed border-slate-300 text-slate-500',
  leave:     'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20',
  depot:     'bg-sky-50 border-sky-200 text-sky-800 dark:bg-sky-900/20',
  reserve:   'bg-violet-50 border-violet-200 text-violet-800 dark:bg-violet-900/20',
  cancelled: 'bg-red-50 border-red-200 text-red-700 line-through',
};

/** Map an ISO date string to its short Turkish day label (Pzt..Paz). */
export function dayLabel(iso) {
  try { return DAY_NAMES_SHORT[(new Date(iso).getDay() + 6) % 7]; } catch { return ''; }
}


/**
 * Turkish-fold a string for diacritic-/case-insensitive search.
 *
 *   normalizeTr("Hüyük") === normalizeTr("huyuk")
 *
 * Handles the locale-specific quirks JS' default `toLowerCase()` gets wrong
 * (`İ → i` not `İ → ı`, the dotless `ı`, soft-g `ğ`, cedilla `ş/ç`, umlaut
 * `ü/ö`). Applied to both the haystack and needle before `includes()`.
 */
const TR_FOLD_MAP = {
  'İ': 'i', 'I': 'i', 'ı': 'i',
  'Ğ': 'g', 'ğ': 'g',
  'Ü': 'u', 'ü': 'u',
  'Ş': 's', 'ş': 's',
  'Ö': 'o', 'ö': 'o',
  'Ç': 'c', 'ç': 'c',
  'Â': 'a', 'â': 'a',
  'Î': 'i', 'î': 'i',
  'Û': 'u', 'û': 'u',
};
export function normalizeTr(str) {
  if (!str) return '';
  let out = '';
  for (const ch of String(str)) {
    out += TR_FOLD_MAP[ch] ?? ch;
  }
  return out.toLowerCase();
}
